import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  quoteText?: string;
  imageUrls?: string[];
  sources?: string[];
  isStreaming?: boolean;
  createdAt: Date;
}

interface ChatState {
  messages: Record<string, ChatMessage[]>; // documentId -> messages
  isStreaming: boolean;
  highlightedText: string | null;
  /**
   * The AI tier key the user picked in the composer ('' until they choose).
   *
   * Empty rather than a hardcoded default on purpose: the previous store shipped
   * a persisted model id that outlived the model itself, so every returning user
   * carried a dead selection until an effect corrected it. '' simply means
   * "use the first tier this plan grants".
   */
  selectedTier: string;
}

interface ChatActions {
  addMessage: (documentId: string, message: ChatMessage) => void;
  updateMessage: (documentId: string, messageId: string, content: string) => void;
  setMessages: (documentId: string, messages: ChatMessage[]) => void;
  clearMessages: (documentId: string) => void;
  setIsStreaming: (isStreaming: boolean) => void;
  setHighlightedText: (text: string | null) => void;
  setSelectedTier: (tier: string) => void;
  getMessages: (documentId: string) => ChatMessage[];
}

type ChatStore = ChatState & ChatActions;

export const useChatStore = create<ChatStore>()(
  persist(
    (set, get) => ({
      messages: {},
      isStreaming: false,
      highlightedText: null,
      selectedTier: '',

      addMessage: (documentId, message) =>
        set((state) => ({
          messages: {
            ...state.messages,
            [documentId]: [...(state.messages[documentId] || []), message],
          },
        })),

      updateMessage: (documentId, messageId, content) =>
        set((state) => ({
          messages: {
            ...state.messages,
            [documentId]: (state.messages[documentId] || []).map((msg) =>
              msg.id === messageId ? { ...msg, content, isStreaming: false } : msg,
            ),
          },
        })),

      setMessages: (documentId, messages) =>
        set((state) => ({
          messages: {
            ...state.messages,
            [documentId]: messages,
          },
        })),

      clearMessages: (documentId) =>
        set((state) => ({
          messages: {
            ...state.messages,
            [documentId]: [],
          },
        })),

      setIsStreaming: (isStreaming) => set({ isStreaming }),
      setHighlightedText: (text) => set({ highlightedText: text }),
      setSelectedTier: (tier) => set({ selectedTier: tier }),

      getMessages: (documentId) => get().messages[documentId] || [],
    }),
    {
      name: 'examing-chat',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ selectedTier: state.selectedTier } as ChatStore),
    },
  ),
);
